import React, { useEffect, useState, useRef } from 'react';
import productService from '../../../../services/productService';
import { buildProductQuery, mapProductFromApi } from '../../../../models/productModel';
import AdicionarProduto from './Produtos/AdicionarProduto';
import BackWithSidebar from '../../../../components/BackWithSidebar';
import GeneralIcon from '../../../../assets';
import SearchBar from '../../../../components/SearchBar';
import { getImageUrl } from '../../../../services/imageStore';

function EmptyState() {
  return (
    <div className="text-center py-16 text-gray-400">
      <GeneralIcon iconName='vazio' className="mx-auto mb-4 h-40 w-40" />
      <div className="text-sm">Nenhum produto encontrado</div>
    </div>
  );
}

export default function Produtos() {
  const [query, setQuery] = useState('');
  const [filtersMenuOpen, setFiltersMenuOpen] = useState(false);
  const [availableFilters, setAvailableFilters] = useState({});
  const [activeFilters, setActiveFilters] = useState({});
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [productToEdit, setProductToEdit] = useState(null);
  const [imageUrls, setImageUrls] = useState({});



  const debounceRef = useRef(null);

  useEffect(() => {
    // load available filters for the store (store id could be obtained from context)
    let mounted = true;
    productService.fetchProductFilters().then(res => {
      if (!mounted) return;
      // expect res to be an object like { category: [...], tags: [...], status: [...] }
      setAvailableFilters(res || {});
    }).catch(() => {});
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    // live search with debounce
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      doSearch();
    }, 350);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query, activeFilters]);

  async function doSearch() {
    setLoading(true);
    try {
      const params = buildProductQuery({ q: query, filters: activeFilters, page: 1, limit: 50 });
      const res = await productService.searchProducts(params);
      
      const items = res && (res.items || res.data || res) || [];
      const mappedProducts = items.map(mapProductFromApi);
      setProducts(mappedProducts);
      
      // Resolve image URLs for products with blob keys
      const urlEntries = await Promise.all(mappedProducts.map(async (p) => {
        if (p.image?.key) {
          const url = await getImageUrl(p.image.key);
          return [p.id, url];
        } else if (typeof p.image === 'string') {
          return [p.id, p.image];
        } else if (p.image?.url) {
          return [p.id, p.image.url];
        }
        return [p.id, null];
      }));
      
      setImageUrls(Object.fromEntries(urlEntries));
    } catch (err) {
      console.error('searchProducts error', err);
      setProducts([]);
      setImageUrls({});
    } finally { 
      setLoading(false); 
    }
  }

  function toggleFilterOption(key, value) {
    setActiveFilters(prev => {
      const cur = prev[key] || [];
      const exists = cur.includes(value);
      const next = exists ? cur.filter(x => x !== value) : [...cur, value];
      return { ...prev, [key]: next };
    });
  }

  function removeChip(key, value) {
    setActiveFilters(prev => {
      const cur = prev[key] || [];
      const next = cur.filter(x => x !== value);
      const copy = { ...prev };
      if (next.length) copy[key] = next; else delete copy[key];
      return copy;
    });
  }

function handleAddNew() {
  setProductToEdit(null); // garante que não há produto selecionado
  setShowAdd(true);
}

  async function handleDelete(id) {
  if (!window.confirm("Tem certeza que deseja excluir este produto?")) return;

  await productService.deleteProduct(id);
  await doSearch(); // recarrega a lista
}

async function handleEdit(product) {
  try {
    // busca o produto completo com suas variantes
    const fullProduct = await productService.fetchProductById(product.id);
    console.log('Produto completo com variantes:', fullProduct);  // Verifique isso no console
    setProductToEdit(fullProduct || product); // fallback para o produto original
    setShowAdd(true);           // abre modal
  } catch (err) {
    console.error("Erro ao carregar produto:", err);
    setProductToEdit(product);
    setShowAdd(true);
  }
}



  return (
    <div className="p-4 max-w-3xl mx-auto">
      <div className="flex items-center gap-3 mb-4">
        <BackWithSidebar to="/dashboard" className="p-2 rounded-full hover:bg-gray-100" />
        <h2 className="text-lg font-semibold">Produtos de Mercadoria</h2>
      </div>

      <div className="bg-white rounded p-4 shadow-sm">
        <div className="flex gap-3 items-center">
          <SearchBar
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Pesquisar por produtos"
            className="flex-1"
          />
          <div className="relative">
            <button type="button" onClick={() => setFiltersMenuOpen(s => !s)} className="border rounded px-3 py-2 bg-gray-50">Filtrar</button>
            {filtersMenuOpen && (
              <div className="absolute right-0 mt-2 w-64 bg-white border rounded shadow p-3 z-20">
                {Object.keys(availableFilters).length === 0 && <div className="text-sm text-gray-500">Sem filtros disponíveis</div>}
                {Object.entries(availableFilters).map(([key, options]) => (
                  <div key={key} className="mb-3">
                    <div className="text-xs font-medium text-gray-600 mb-1">{key}</div>
                    <div className="flex flex-col gap-1 max-h-40 overflow-auto">
                      {options.map(opt => (
                        <label key={opt} className="text-sm flex items-center gap-2">
                          <input type="checkbox" checked={(activeFilters[key] || []).includes(opt)} onChange={() => toggleFilterOption(key, opt)} />
                          <span className="text-sm">{opt}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
                <div className="text-right">
                  <button type="button" onClick={() => setFiltersMenuOpen(false)} className="text-sm text-gray-600">Fechar</button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* chips */}
        <div className="mt-3 flex gap-2 flex-wrap">
          {Object.entries(activeFilters).flatMap(([k, arr]) => arr.map(v => ({ k, v }))).map(({ k, v }) => (
            <div key={k + ':' + v} className="bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-sm flex items-center gap-2">
              <span>{v}</span>
              <button onClick={() => removeChip(k, v)} className="text-purple-700">×</button>
            </div>
          ))}
        </div>

        <div className="mt-4">
          {loading ? (
            <div className="text-center py-8 text-sm text-gray-500">Carregando...</div>
          ) : products.length === 0 ? (
            <EmptyState />
          ) : (
            <div className="space-y-3">
              {products.map(p => (
                <div key={p.id} className="border rounded p-3 flex gap-3 items-center">
                  <div className="w-16 h-16 bg-gray-50 rounded flex items-center justify-center overflow-hidden">
                    {imageUrls[p.id] ? (
                      <img src={imageUrls[p.id]} alt={p.title} className="object-cover w-full h-full" />
                    ) : (
                      <div className="text-gray-300">img</div>
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="font-medium">{p.title}</div>
                    <div className="text-sm text-gray-500">{p.subtitle}</div>
                    {p.category && <div className="text-xs inline-block mt-2 px-2 py-1 bg-gray-100 rounded text-gray-600">{p.category}</div>}
                  </div>
<div className="flex flex-col gap-2">
  <button
    onClick={() => handleEdit(p)}
    className="p-2 rounded border text-purple-600"
  >
    <GeneralIcon iconName="editar" className="h-5 w-5" />
  </button>

  <button
    onClick={() => handleDelete(p.id)}
    className="p-2 rounded border text-red-500"
  >
    <GeneralIcon iconName="lixeira" className="h-5 w-5" />
  </button>
</div>


                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-6 text-center">
  <button onClick={handleAddNew} className="py-3 px-6 rounded bg-purple-600 text-white">
    + Adicionar um novo produto
  </button>
</div>
      </div>
     {showAdd && (
  <AdicionarProduto
    product={productToEdit}   // se existir, o modal vai preencher os campos
    onClose={() => setShowAdd(false)}
    onSave={doSearch}          // atualiza lista após salvar
  />
)}

    </div>
  );
}

